import React, {FormEvent, ReactNode, useEffect, useState} from 'react';
import {useZooPassAppContext} from "../../Context/ZooPassAppContext";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import showdown from "showdown";
import {StepTitleContent} from "./StepTitleContent";


export interface GuestMemberLookupInputValues {
    membershipId: string;
    ln: string;
}

const DefaultGuestMemberLookupInputValues: GuestMemberLookupInputValues = {
    ln: "", membershipId: ""
}

export interface RenewalGiftMemberLookupFormSegmentProperties {
    onSubmit?: (values : GuestMemberLookupInputValues) => void;
}

export const RenewalGiftMemberLookupFormSegment: React.FC<RenewalGiftMemberLookupFormSegmentProperties> = ({onSubmit}) => {

    const converter = new showdown.Converter();
    
    let {
        dictionary,
        loading
    } = useZooPassAppContext();

    const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if(onSubmit != null && lookupSubmitEnabled){
            onSubmit(lookupInputValues);
        }
    }

    ////////////////////////////////////////////////////////////////
    // Search Form Input Values
    const [lookupInputValues, setLookupInputValues] = useState<GuestMemberLookupInputValues>(DefaultGuestMemberLookupInputValues);
    const [lookupSubmitEnabled, setLookupSubmitEnabled] = useState(false);
    useEffect(() => {
        // console.group("Lookup Values Changed");
        // console.log("Last Name: \"" + lookupInputValues.ln + "\" (" + lookupInputValues.ln.length + ")");
        const lnPass = lookupInputValues.ln.length >= 2;
        // console.info("Last Name Passes: ", lnPass);
        // console.log("Membership ID: \"" + lookupInputValues.membershipId + "\" (" + lookupInputValues.membershipId.length + ")");
        const memberPass = lookupInputValues.membershipId.length >= 2;
        // console.info("Membership Passes: ", memberPass);
        const buttonEnabled = lnPass && memberPass;
        // console.info("Button Enabled: ", buttonEnabled);
        // console.groupEnd();
        setLookupSubmitEnabled(buttonEnabled);
    }, [lookupInputValues.ln, lookupInputValues.membershipId]);
    // END: Search Form Input Values
    ////////////////////////////////////////////////////////////////

    return !loading && (<div
        className="row g-4 align-items-center justify-content-center">
        <div className="col bg-white col-12 col-md-10 col-lg-9 col-xl-7 col-xxl-6">
            <div className="m-2 p-2">
            <h3>{dictionary.get('ZooPass:StepLayout:02:Renew:Form:Heading')}</h3>
            <Form onSubmit={(event) => handleSearchSubmit(event)}>
                <div className="mb-3 form-group required">
                    <label htmlFor="membershipId"
                           className="form-label">{dictionary.get('ZooPass:StepLayout:02:Renew:Form:Membership:Label')}</label>
                    <input type="text" className="form-control" name="membershipId" id="membershipId" required={true}
                           placeholder={dictionary.get('ZooPass:StepLayout:02:Renew:Form:Membership:Placeholder')}
                           onChange={(event) => setLookupInputValues({
                               membershipId: event.target.value,
                               ln: lookupInputValues.ln
                           })}/>
                </div>
                <div className="mb-3 form-group required">
                    <label htmlFor="ln" className="form-label">{dictionary.get('ZooPass:StepLayout:02:Renew:Form:LastName:Label')}</label>
                    <input type="text" required={true} className="form-control" name="ln" id="ln"
                           placeholder={dictionary.get('ZooPass:StepLayout:02:Renew:Form:LastName:Placeholder')} 
                           onChange={(event) => setLookupInputValues({
                               ln: event.target.value, 
                               membershipId: lookupInputValues.membershipId})}/>
                </div>
                <div className="mb-3 form-group">
                    <Button type="submit" disabled={!lookupSubmitEnabled}
                            className="btn btn-default border-2 border-black px-5 rounded-0 zoopass--step8--choose-pass-button">
                        {dictionary.get('ZooPass:StepLayout:02:Renew:Form:SubmitButton:Text')}
                    </Button>
                </div>
            </Form>
            <div className="d-flex my-2 p-1 align-items-center justify-content-center" 
                 dangerouslySetInnerHTML={{__html: converter.makeHtml(dictionary.get("ZooPass:StepLayout:02:Renew:ContactMembership:MARKDOWN") ?? "")}}>
                
            </div>
            </div>
        </div>
    </div>);
}

export interface RenewalLoginSegmentProperties {
    clickCallback?: () => void;
    titleName?: string;
}

export const RenewalLoginSegment: React.FC<RenewalLoginSegmentProperties> = ({clickCallback, titleName}) => {
    let {
        dictionary,
        textTitles,
        loading
    } = useZooPassAppContext();

    const handleLoginClick = () => {
        if (clickCallback != null) {
            clickCallback();
        }
    }

    return !loading && (<>
        <StepTitleContent textTitle={textTitles.get(titleName ?? "ZooPass:PageContent:02:Renew:Login")}/>
        <div
            className="row g-4 align-items-center justify-content-center row-cols-lg-2 row-cols-1">
            <div className="col">
                <div className="m-4 d-flex justify-content-center">
                    <div className="m-5">
                        <h3>{dictionary.get('ZooPass:StepLayout:02:Renew:LogIn:Heading')}</h3>
                        <Button type="button" tabIndex={0}
                                className="btn btn-default w-100 border-2 border-black rounded-0 zoopass--step8--choose-pass-button"
                                onClick={handleLoginClick}>{dictionary.get('ZooPass:StepLayout:02:Renew:LogIn:ButtonText')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    </>);
}

export interface RenewalStartANewPassSegmentProperties {
    clickCallback?: () => void;
}

export const RenewalStartANewPassSegment: React.FC<RenewalStartANewPassSegmentProperties> = ({clickCallback}) => {

    let {
        dictionary,
        loading
    } = useZooPassAppContext();

    const handleNewItemClick = () => {
        if (clickCallback != null) {
            clickCallback();
        }
    };

    return !loading && (<div
        className="row g-4 align-items-center justify-content-center row-cols-xl-4 row-cols-lg-3 row-cols-md-2 row-cols-1">
        <div className="col">
            <h3>{dictionary.get('ZooPass:StepLayout:02:Renew:NewHeading')}</h3>
            <Button type="button" tabIndex={0}
                    className="btn btn-default w-100 border-2 border-black rounded-0 zoopass--step8--choose-pass-button"
                    onClick={handleNewItemClick}
            >{dictionary.get('ZooPass:StepLayout:02:Renew:NewButtonText')}
            </Button>
        </div>
    </div>);
}

export const RenewalHrSegment: React.FC = () => {
    return (<div
        className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-2 row-cols-1">
        <div className="col">
            <hr/>
        </div>
    </div>);
}

export const RenewalLoadingSegment: React.FC = () => {
    let {
        loading
    } = useZooPassAppContext();

    return !loading && (<LoadingSegment />);
}

export const LoadingSegment: React.FC = () => {
    let {
        dictionary
    } = useZooPassAppContext();

    return (<>
        <div
            className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-2 row-cols-1">
            <div className="col">
                <div className="m-4 d-flex justify-content-center">
                    <div className="spinner-border" style={{width: '3rem', height: '3rem'}} role="status">
                        <span className="sr-only">{dictionary.get('ZooPass:StepLayout:02:Renew:LoadingText')}</span>
                    </div>
                </div>
            </div>
        </div>
    </>);
}